import { QueryClient } from '@tanstack/react-query';

import { AiAnswerCheckStatus, GetQuestionDetailWithNearSimplyQuestionsQuery } from '@/types/bff/gql-schema';
import { AnswererType, QnaDetailVo } from '@/types/qna';
import { displayedAt, sortImageBlindTypeFormat } from '@/utils/format';

import { useGetQuestionDetailWithNearSimplyQuestionsQuery } from './mobile/qna.gql.impl';

export const parseQnaDetailVo = (qnaDetail: GetQuestionDetailWithNearSimplyQuestionsQuery): QnaDetailVo | undefined => {
  if (!qnaDetail.questionV2) return undefined;
  return {
    ...qnaDetail.questionV2,
    nameAndDate: `${qnaDetail.questionV2.user.name} • ${displayedAt(qnaDetail.questionV2.createdAt)}`,
    answers: qnaDetail.questionV2.answers.map((answer) => {
      return {
        ...answer,
        createdDate: displayedAt(answer.createdAt),
        reviews: answer.reviews.map((review) => {
          return {
            ...review,
            content: review.content ?? '',
            createdAt: displayedAt(review.createdAt),
            updatedAt: displayedAt(review.updatedAt),
          };
        }),
        isReviewViewable: answer.answerer.answererType === AnswererType.Nurse,
        hasReview: answer.reviews.length > 0,
      };
    }),
    attachments: sortImageBlindTypeFormat(qnaDetail.questionV2.attachments),
    isNotAnswered: !qnaDetail.questionV2.answererCount,
    paymentTotal: qnaDetail.questionV2.paymentTotal,
    paymentInfo: qnaDetail.questionV2.paymentInfo,
    isAiAnswerChecked: qnaDetail.questionV2.aiAnswer?.checkStatus === AiAnswerCheckStatus.Checked,
  };
};

const useGetQnaDetailWithNearSimplyQuestions = (questionPid: string) => {
  const {
    data: qnaDetail,
    isLoading,
    refetch: refetchQnaDetail,
  } = useGetQuestionDetailWithNearSimplyQuestionsQuery(
    {
      questionPid,
    },
    {
      select: parseQnaDetailVo,
    }
  );
  return {
    qnaDetail,
    isLoading,
    refetchQnaDetail,
  };
};

export default useGetQnaDetailWithNearSimplyQuestions;

useGetQnaDetailWithNearSimplyQuestions.getKey = (questionPid: string) => {
  return useGetQuestionDetailWithNearSimplyQuestionsQuery.getKey({ questionPid });
};

useGetQnaDetailWithNearSimplyQuestions.fetchQuery = async (queryClient: QueryClient, questionPid: string) => {
  return await queryClient.fetchQuery(
    useGetQuestionDetailWithNearSimplyQuestionsQuery.getKey({ questionPid }),
    useGetQuestionDetailWithNearSimplyQuestionsQuery.fetcher({
      questionPid,
    })
  );
};
